<template>
  <Teleport to="body">
    <div v-if="show" class="confirm-overlay" role="dialog" aria-modal="true">
      <div class="confirm-card" :class="{ desktop: $isDesktop }">
        <h2 class="confirm-title">
          <translate>Is this what you're looking for?</translate>
        </h2>

        <div class="confirm-details">
          <!-- Location -->
          <div v-if="searchData.location" class="detail-row">
            <span class="detail-icon">📍</span>
            <span class="detail-text">{{ searchData.location }}</span>
          </div>

          <!-- Dates -->
          <div v-if="searchData.arrive" class="detail-row">
            <span class="detail-icon">📅</span>
            <span class="detail-text">
              <i18n-t keypath="{fromDate} - {toDate}">
                <template #fromDate>
                  <pu-utc-date :date="searchData.arrive" options="dayMonth" />
                </template>
                <template #toDate>
                  <pu-utc-date :date="searchData.depart" options="dayMonth" />
                </template>
              </i18n-t>
            </span>
          </div>

          <!-- Party -->
          <div v-if="hasParty" class="detail-row">
            <span class="detail-icon">👥</span>
            <span class="detail-text">
              <translate
                v-if="searchData.adults"
                :tt="{ people: searchData.adults }"
              >
                {people, plural, one{# adult} other{# adults}}
              </translate>
              <template v-if="searchData.adults && childCount">, </template>
              <translate v-if="childCount" :tt="{ childrenCount: childCount }">
                {childrenCount, plural, one{# child} other{# children}}
              </translate>
            </span>
          </div>

          <!-- Child ages - each with dynamic emoji -->
          <div
            v-for="childIndex in childCount"
            :key="'child-' + childIndex"
            class="detail-row"
          >
            <span class="detail-icon">{{
              getChildEmoji(localChildAges[childIndex - 1])
            }}</span>
            <select
              v-model="localChildAges[childIndex - 1]"
              class="age-select"
              :class="{
                error: validationError && !isAgeValid(childIndex - 1),
              }"
              data-test="ai-confirm-child-age"
            >
              <option value="" disabled>---</option>
              <translate
                v-for="childAge in ageOptions"
                :key="childAge"
                tag="option"
                :value="childAge"
                :tt="{ childAge }"
              >
                {childAge} years old
              </translate>
            </select>
          </div>
          <transition name="fade">
            <translate v-if="validationError" tag="div" class="input-error-msg">
              Please enter ages for all children
            </translate>
          </transition>

          <!-- Dogs -->
          <div v-if="searchData.dogs && searchData.dogs > 0" class="detail-row">
            <span class="detail-icon">🐕</span>
            <translate
              tag="span"
              class="detail-text"
              :tt="{ dogs: searchData.dogs }"
            >
              {dogs} dogs
            </translate>
          </div>

          <!-- Accommodation type -->
          <div v-if="searchData.accommodationType" class="detail-row">
            <span class="detail-icon">🏕️</span>
            <span class="detail-text">{{ searchData.accommodationType }}</span>
          </div>

          <!-- Features -->
          <div
            v-if="searchData.features && searchData.features.length > 0"
            class="detail-row"
          >
            <span class="detail-icon">✨</span>
            <span class="detail-text">{{
              searchData.features.join(', ')
            }}</span>
          </div>

          <p class="helper-text">
            <translate
              >You can refine dates and filters on the search page</translate
            >
          </p>
        </div>

        <div class="confirm-actions">
          <button
            type="button"
            class="btn-reject"
            data-test="ai-confirm-reject"
            @click="handleReject"
          >
            <translate>That's not what I meant</translate>
          </button>
          <button
            type="button"
            class="btn-confirm"
            :disabled="!canConfirm"
            data-test="ai-confirm-submit"
            @click="handleConfirm"
          >
            <translate>Yes, search!</translate>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'

export interface AiSearchConfirmData {
  location?: string
  arrive?: string
  depart?: string
  adults?: number
  children?: number
  childAges?: number[]
  needsChildAges?: boolean
  dogs?: number
  accommodationType?: string
  features?: string[]
}

const props = defineProps<{
  show: boolean
  searchData: AiSearchConfirmData
}>()

const emit = defineEmits<{
  confirm: [childAges?: number[]]
  reject: []
}>()

const { $isDesktop } = useNuxtApp()

// Age options 0-18
const ageOptions = [...Array(19).keys()]

// Local state for child ages
const localChildAges = ref<(number | '')[]>([])
const validationError = ref(false)

// Number of children
const childCount = computed(() => props.searchData.children || 0)

// Reset local ages when dialog opens or children count changes
watch(
  () => [props.show, props.searchData.children],
  () => {
    if (props.show) {
      // Debug: log the search data
      // eslint-disable-next-line no-console
      console.log('[AiSearchConfirmDialog] searchData:', props.searchData)

      // Initialize with existing ages or empty
      const existingAges = props.searchData.childAges || []
      localChildAges.value = Array.from({ length: childCount.value }, (_, i) =>
        existingAges[i] !== undefined ? existingAges[i] : '',
      )

      // eslint-disable-next-line no-console
      console.log(
        '[AiSearchConfirmDialog] initialized ages:',
        localChildAges.value,
        'from:',
        existingAges,
      )

      validationError.value = false
    }
  },
  { immediate: true },
)

// Party text
const hasParty = computed(() => {
  return (
    (props.searchData.adults && props.searchData.adults > 0) ||
    childCount.value > 0
  )
})

// Get emoji based on child's age
function getChildEmoji(age: number | ''): string {
  if (age === '' || age === undefined) return '🧒'
  if (age <= 2) return '👶' // baby
  if (age <= 12) return '🧒' // child
  return '🧑' // teen
}

// Validation
function isAgeValid(index: number): boolean {
  const age = localChildAges.value[index]
  return age !== '' && age !== undefined && age >= 0
}

const allAgesValid = computed(() => {
  // Only require ages if backend says we need them (AI defaulted ages)
  if (!props.searchData.needsChildAges || childCount.value === 0) {
    return true
  }
  // When ages are required, all must be selected
  return localChildAges.value
    .slice(0, childCount.value)
    .every((age) => age !== '' && age >= 0)
})

const canConfirm = computed(() => allAgesValid.value)

// Handlers
function handleConfirm() {
  if (props.searchData.needsChildAges && !allAgesValid.value) {
    validationError.value = true
    return
  }

  // Pass child ages if any children exist (respects user edits)
  if (childCount.value > 0) {
    // Filter out empty ages and convert to numbers
    const ages = localChildAges.value
      .slice(0, childCount.value)
      .map((a) => (a === '' ? null : Number(a)))
      .filter((a): a is number => a !== null)

    if (ages.length > 0) {
      emit('confirm', ages)
      return
    }
  }
  emit('confirm')
}

function handleReject() {
  emit('reject')
}
</script>

<style scoped>
.confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.confirm-card {
  background: white;
  border-radius: 12px;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 20px 40px -4px rgba(0, 0, 0, 0.2);
  max-width: 360px;
  max-height: calc(100dvh - 2rem);
  width: calc(100% - 2rem);
  margin: 0 1rem;
  animation: slideUp 0.2s ease-out;
  display: flex;
  flex-direction: column;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.confirm-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--colour-text-default, #1f2937);
  padding: 0.85rem 1.25rem;
  margin: 0;
  text-align: center;
  border-bottom: 1px solid var(--colour-table-borders, #e5e7eb);
  flex-shrink: 0;
}

.confirm-details {
  padding: 0.5rem 1.25rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.detail-row {
  display: flex;
  align-items: baseline;
  gap: 0.65rem;
  padding: 0.4rem 0;
}

.detail-icon {
  font-size: 1.1rem;
  flex-shrink: 0;
  width: 1.5rem;
  text-align: center;
}

.detail-text {
  font-size: 1rem;
  color: var(--colour-text-default, #374151);
  line-height: 1.4;
}

/* Child age select */
.age-select {
  flex: 1;
  padding: 0.4rem 0.5rem;
  font-size: 0.95rem;
  border: 1px solid var(--colour-light-grey, #d1d5db);
  border-radius: 4px;
  background: white;
  color: var(--colour-text-default, #374151);
  cursor: pointer;
  min-width: 4rem;
}

.age-select:focus {
  outline: none;
  border-color: var(--colour-focus, #0072bc);
}

.age-select.error {
  border-color: var(--colour-error, #dc2626);
  background: #fef2f2;
}

.input-error-msg {
  color: var(--colour-error, #dc2626);
  font-size: 0.7rem;
  margin-top: 0.2rem;
}

.helper-text {
  font-size: 0.85rem;
  color: var(--colour-text-light, #6b7280);
  text-align: center;
  margin: 0.65rem 0 0;
  padding-top: 0.5rem;
  border-top: 1px dashed var(--colour-table-borders, #e5e7eb);
}

/* Actions */
.confirm-actions {
  display: flex;
  gap: 0.6rem;
  padding: 0.75rem 1.25rem 1rem;
  flex-shrink: 0;
}

.btn-reject,
.btn-confirm {
  flex: 1;
  padding: 0.7rem 0.5rem;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-reject {
  background: white;
  border: 1px solid var(--colour-light-grey, #d1d5db);
  color: var(--colour-text-default, #4b5563);
}

.btn-reject:hover {
  background: #f9fafb;
  border-color: var(--colour-text-light, #9ca3af);
}

.btn-confirm {
  background: var(--colour-cta-green, #16a34a);
  border: none;
  color: white;
}

.btn-confirm:hover:not(:disabled) {
  background: var(--colour-cta-green-dark, #15803d);
}

.btn-confirm:disabled {
  background: var(--colour-light-grey, #d1d5db);
  color: var(--colour-text-light, #9ca3af);
  cursor: not-allowed;
}

/* Desktop adjustments */
.confirm-card.desktop {
  max-width: 380px;
  margin: 0;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
